import { useState } from "react";
import {
  EditButton,
  TopToolbar,
  ListButton,
  Button,
  useRecordContext,
  Confirm,
  useNotify,
  useRefresh,
  usePermissions,
} from "react-admin";
import PrintIcon from "@mui/icons-material/Print";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import UndoIcon from "@mui/icons-material/Undo";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MuiButton from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";

const PrintRazaButton = () => {
  const record = useRecordContext<{ id?: string; razaGranted?: string | null }>();
  const { permissions } = usePermissions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [includeCommitments, setIncludeCommitments] = useState(false);
  const canItsOneView = hasPermission(permissions, "itsOneView.view");

  if (!record?.id || record.razaGranted) return null;

  const openPrint = () => {
    const qs = includeCommitments && canItsOneView ? "?commitments=1" : "";
    window.open(`#/raza-request-print/${record.id}${qs}`, "_blank");
    setDialogOpen(false);
    setIncludeCommitments(false);
  };

  return (
    <>
      <Button label="Raza form" onClick={() => setDialogOpen(true)}>
        <PrintIcon />
      </Button>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setIncludeCommitments(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Print raza form</DialogTitle>
        <DialogContent>
          {canItsOneView ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeCommitments}
                  onChange={(e) => setIncludeCommitments(e.target.checked)}
                />
              }
              label="Include commitments"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Open the form for printing. Commitment stats require One View access.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDialogOpen(false)}>Cancel</MuiButton>
          <MuiButton variant="contained" onClick={openPrint}>
            Open print
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RazaRequestsShowActions = () => {
  const record = useRecordContext<{ id?: string; razaGranted?: string | null }>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmGrantOpen, setConfirmGrantOpen] = useState(false);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [revertLoading, setRevertLoading] = useState(false);

  const handleGrantRaza = async () => {
    if (!record?.id) return;
    setGrantLoading(true);
    try {
      await callApi({
        location: `razaRequests/${record.id}/grant-raza`,
        method: "PUT",
        data: {},
      });
      notify("Raza marked as granted", { type: "success" });
      setConfirmGrantOpen(false);
      refresh();
    } catch {
      notify("Something went wrong", { type: "error" });
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevertRaza = async () => {
    if (!record?.id) return;
    setRevertLoading(true);
    try {
      await callApi({
        location: `razaRequests/${record.id}/revert-raza`,
        method: "PUT",
        data: {},
      });
      notify("Raza grant cleared; request is pending again", { type: "success" });
      setConfirmRevertOpen(false);
      refresh();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: unknown } } };
      const m = ax.response?.data?.message;
      const msg =
        typeof m === "string" && m.trim()
          ? m.trim()
          : Array.isArray(m)
            ? m.filter((x): x is string => typeof x === "string").join(", ")
            : "Could not revert raza";
      notify(msg, { type: "error" });
    } finally {
      setRevertLoading(false);
    }
  };

  return (
    <>
      <TopToolbar>
        <ListButton />
        <EditButton />
        <PrintRazaButton />
        {record?.id && !record.razaGranted && (
          <Button
            label="Mark raza granted"
            onClick={() => setConfirmGrantOpen(true)}
            sx={{ color: "success.main" }}
          >
            <DoneAllIcon />
          </Button>
        )}
        {record?.id && record.razaGranted ? (
          <Button
            label="Revert raza granted"
            onClick={() => setConfirmRevertOpen(true)}
            sx={{ color: "warning.main" }}
          >
            <UndoIcon />
          </Button>
        ) : null}
      </TopToolbar>
      <Confirm
        isOpen={confirmGrantOpen}
        loading={grantLoading}
        title="Confirm Raza granted"
        content="Are you sure you want to mark Raza as granted for this request?"
        onConfirm={() => void handleGrantRaza()}
        onClose={() => setConfirmGrantOpen(false)}
      />
      <Confirm
        isOpen={confirmRevertOpen}
        loading={revertLoading}
        title="Revert Raza granted"
        content="Clear the grant date for this request, clear the raza flag on any linked booking, and update Google Calendar titles to pending again. Events and hall links are unchanged."
        onConfirm={() => void handleRevertRaza()}
        onClose={() => setConfirmRevertOpen(false)}
      />
    </>
  );
};

export default RazaRequestsShowActions;
